import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, MapPin, CreditCard, Banknote, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, orderDetails, setOrderDetails, updateQuantity, removeFromCart, getSubtotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' or 'instore'
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');

  const subtotal = getSubtotal();
  const total = subtotal; // No delivery fee - collection only

  const handleChange = (e) => {
    setOrderDetails({ ...orderDetails, [e.target.name]: e.target.value });
  };

  const handleCheckout = async () => {
    if (!orderDetails.customer_name || !orderDetails.email || !orderDetails.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      // Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        customer_name: orderDetails.customer_name,
        email: orderDetails.email,
        phone: orderDetails.phone,
        order_type: 'collection',
        payment_method: paymentMethod,
        items: cartItems,
        special_instructions: orderDetails.special_instructions
      });

      if (paymentMethod === 'online') {
        // Redirect to Stripe checkout
        const checkoutResponse = await axios.post(`${API}/checkout/session`, {
          order_id: orderResponse.data.id,
          origin_url: window.location.origin
        });
        if (checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        // Pay in store - show confirmation
        setOrderNumber(orderResponse.data.id.slice(0, 8).toUpperCase());
        setOrderConfirmed(true);
        clearCart();
        toast.success('Order placed successfully! Pay when you collect.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to process order. Please try again.');
      setLoading(false);
    }
  };

  // Order confirmed view (for pay in store)
  if (orderConfirmed) {
    return (
      <div className="min-h-screen pt-24 md:pt-32 bg-gradient-to-b from-silver-bright to-white" data-testid="cart-page">
        <div className="container-elegant py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-gray-800 mb-4">
              Order Confirmed!
            </h1>
            <p className="text-gray-600 text-lg mb-2">
              Thank you, {orderDetails.customer_name}
            </p>
            <div className="card-elegant p-6 my-8">
              <div className="space-y-4">
                <div className="flex justify-between border-b border-malva/10 pb-3">
                  <span className="text-gray-500">Order Number</span>
                  <span className="text-gray-800 font-mono font-bold">#{orderNumber}</span>
                </div>
                <div className="flex justify-between border-b border-malva/10 pb-3">
                  <span className="text-gray-500">Total Amount</span>
                  <span className="text-malva-dark font-bold">£{total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment</span>
                  <span className="text-gray-800">Pay at Collection</span>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-left">
                  <p className="text-amber-800 font-medium">Collection Time</p>
                  <p className="text-amber-700 text-sm">Your order will be ready in 30-45 minutes</p>
                </div>
              </div>
            </div>
            <div className="bg-malva/10 rounded-xl p-4 mb-8">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-malva-dark mt-0.5" />
                <div className="text-left">
                  <p className="text-gray-800 font-medium">Collection Address</p>
                  <p className="text-gray-600 text-sm">45-47 York St, Twickenham, TW1 3LP</p>
                </div>
              </div>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              A confirmation email has been sent to {orderDetails.email}
            </p>
            <Link to="/menu" className="btn-primary inline-flex items-center gap-2">
              Continue Browsing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Empty cart view
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen pt-24 md:pt-32 bg-gradient-to-b from-silver-bright to-white" data-testid="cart-page">
        <div className="container-elegant py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="w-24 h-24 rounded-full bg-malva/10 flex items-center justify-center mx-auto mb-8">
              <ShoppingBag className="w-12 h-12 text-malva/50" />
            </div>
            <h1 className="font-serif text-3xl text-gray-800 mb-4">Your Cart is Empty</h1>
            <p className="text-gray-500 mb-8">
              Discover our exceptional menu and add some delicious items to your cart.
            </p>
            <Link to="/order" className="btn-primary inline-flex items-center gap-2">
              Browse Menu <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 md:pt-32 bg-gradient-to-b from-silver-bright to-white" data-testid="cart-page">
      <div className="container-elegant py-8 md:py-16">
        <h1 className="font-serif text-3xl md:text-4xl text-gray-800 mb-8">
          {step === 1 ? 'Your Order' : 'Checkout'}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {step === 1 ? (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.menu_item_id} className="card-elegant p-4 md:p-6 flex gap-4" data-testid={`cart-item-${item.menu_item_id}`}>
                    <div className="flex-1">
                      <h3 className="font-serif text-lg text-gray-800 mb-1">{item.name}</h3>
                      <p className="text-malva-dark text-sm">£{item.price.toFixed(2)} each</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 bg-silver-bright rounded-full p-1">
                        <button onClick={() => updateQuantity(item.menu_item_id, item.quantity - 1)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-malva/20 transition-colors" data-testid={`decrease-${item.menu_item_id}`}>
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-gray-800 w-8 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.menu_item_id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-malva/20 transition-colors" data-testid={`increase-${item.menu_item_id}`}>
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button onClick={() => removeFromCart(item.menu_item_id)}
                        className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`remove-${item.menu_item_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="pt-4">
                  <Button onClick={() => setStep(2)} className="w-full btn-primary h-14" data-testid="proceed-to-checkout">
                    Proceed to Checkout <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="card-elegant p-6 md:p-8 space-y-6">
                <button onClick={() => setStep(1)} className="text-malva-dark hover:text-malva-darker transition-colors text-sm">← Back to cart</button>
                
                {/* Collection Info */}
                <div className="bg-malva/10 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-malva-dark mt-0.5" />
                    <div>
                      <p className="text-gray-800 font-medium">Collection Only</p>
                      <p className="text-gray-600 text-sm">45-47 York St, Twickenham, TW1 3LP</p>
                      <p className="text-malva-dark text-sm mt-1">Ready in 30-45 minutes</p>
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="space-y-3">
                  <Label className="text-gray-700 font-medium">How would you like to pay?</Label>
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                    <label 
                      htmlFor="online" 
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'online' 
                          ? 'border-malva-dark bg-malva/5' 
                          : 'border-gray-200 hover:border-malva/50'
                      }`}
                    >
                      <RadioGroupItem value="online" id="online" className="border-malva text-malva" />
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-malva/20 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-malva-dark" />
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">Pay Online Now</p>
                          <p className="text-gray-500 text-sm">Secure card payment via Stripe</p>
                        </div>
                      </div>
                    </label>
                    
                    <label 
                      htmlFor="instore" 
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'instore' 
                          ? 'border-malva-dark bg-malva/5' 
                          : 'border-gray-200 hover:border-malva/50'
                      }`}
                    >
                      <RadioGroupItem value="instore" id="instore" className="border-malva text-malva" />
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-malva/20 flex items-center justify-center">
                          <Banknote className="w-5 h-5 text-malva-dark" />
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">Pay at Collection</p>
                          <p className="text-gray-500 text-sm">Cash or card when you pick up</p>
                        </div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>

                {/* Customer Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name" className="text-gray-700">Full Name *</Label>
                    <Input id="customer_name" name="customer_name" required value={orderDetails.customer_name} onChange={handleChange}
                      placeholder="John Smith" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="checkout-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700">Phone *</Label>
                    <Input id="phone" name="phone" type="tel" required value={orderDetails.phone} onChange={handleChange}
                      placeholder="+44 20 1234 5678" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="checkout-phone" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700">Email *</Label>
                  <Input id="email" name="email" type="email" required value={orderDetails.email} onChange={handleChange}
                    placeholder="john@example.com" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="checkout-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="special_instructions" className="text-gray-700">Special Instructions (Optional)</Label>
                  <Textarea id="special_instructions" name="special_instructions" value={orderDetails.special_instructions} onChange={handleChange}
                    placeholder="Allergies, cooking preferences..." rows={3}
                    className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400 resize-none" data-testid="checkout-instructions" />
                </div>
                <Button onClick={handleCheckout} disabled={loading} className="w-full btn-primary h-14 text-base" data-testid="pay-now-btn">
                  {loading ? 'Processing...' : paymentMethod === 'online' ? `Pay £${total.toFixed(2)} Online` : `Place Order - £${total.toFixed(2)}`} 
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="card-elegant p-6 sticky top-32 bg-gradient-to-b from-white to-silver-bright/30">
              <h2 className="font-serif text-xl text-gray-800 mb-6">Order Summary</h2>
              <div className="space-y-3 mb-6">
                {cartItems.map((item) => (
                  <div key={item.menu_item_id} className="flex justify-between text-sm">
                    <span className="text-gray-500">{item.quantity}x {item.name}</span>
                    <span className="text-gray-800">£{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-malva/10 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-800">£{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Collection</span>
                  <span className="text-green-600 font-medium">Free</span>
                </div>
                <div className="flex justify-between text-lg pt-2 border-t border-malva/10">
                  <span className="text-gray-800 font-serif">Total</span>
                  <span className="text-malva-darker font-serif">£{total.toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-malva/10">
                <div className="flex items-center gap-2 text-gray-500 text-xs">
                  <MapPin className="w-4 h-4" />
                  <span>Collection from: 45-47 York St, Twickenham</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
